import React, {useMemo} from 'react';
import {MatrixEvent} from 'matrix-js-sdk';
import MessageBubble, {groupMessages} from './MessageBubble';
import DateSeparator from "./DateSeparator.tsx";
import SystemMessage from "./SystemMessage.tsx";
import CallNotification from "./CallNotification.tsx";
import {GroupCallTimelineRow} from "../../features/groupCalls/components/GroupCallTimelineRow.tsx";
import {ImageInfo} from "./chatTypes.ts";
import {type CallTimelineEntry, isCallInviteEvent} from "../../utilis/callTimeline.ts";
import {isRtcNotificationEvent} from "../../utilis/rtcNotification.ts";

interface MessageGroup {
    displayDate: string;
    messages: MatrixEvent[];
}

interface MessageGroupComponentProps {
    group: MessageGroup;
    selectedRoomId: string;
    onReply: (messageId: string) => void;
    onEdit: (messageId: string) => void;
    onPrivateReply?: (message: MatrixEvent) => void;
    onForward?: (message: MatrixEvent) => void;
    onOpenForwardedOriginal?: (roomId: string, eventId: string) => void;
    sendReaction: (roomId: string, eventId: string, emoji: string) => Promise<void>;
    removeReaction: (roomId: string, eventId: string, emoji: string) => Promise<void>;
    getClient: () => any;
    scrollToEvent: (eventId: string) => void;
    getCurrentUserId: () => string | null;
    isMessageUnread?: (roomId: string, timestamp: number, eventId: string) => boolean;
    highlightedMessageId?: string | null | undefined;
    isGroupChat?: boolean;
    isAiBotInRoom: boolean;
    isRoomEncrypted: boolean;
    aiBotJoinedAt: number | null;
    searchQuery?: string;
    onOpenImageGallery: (images: ImageInfo[], startIndex: number) => void;
    onMediaLoad?: () => void;
    onViewProfile?: (matrixUserId: string) => void;
    onOpenRoomMention?: (roomIdOrAlias: string) => void;
    onUnresolvedMention?: () => void;
    /** One entry per call in this room, keyed by call id and invite event id. */
    callEntries?: Map<string, CallTimelineEntry>;
    /** Ringing the peer back from a call row. Absent when the room cannot be called. */
    onCallBack?: () => void;
}

const groupImageMessages = (messages: MatrixEvent[]): (MatrixEvent | MatrixEvent[])[] => {
    const result: (MatrixEvent | MatrixEvent[])[] = [];
    let currentImageGroup: MatrixEvent[] = [];
    let currentBatchId: string | null = null;

    const flushImageGroup = () => {
        if (currentImageGroup.length > 0) {
            result.push(currentImageGroup.length > 1 ? [...currentImageGroup] : currentImageGroup[0]);
            currentImageGroup = [];
            currentBatchId = null;
        }
    };

    for (let i = 0; i < messages.length; i++) {
        const message = messages[i];
        const content = message.getContent();
        const isImage = content.msgtype === 'm.image' && !message.isRedacted();
        const batchId: string | undefined = content['org.phonehq.batch_id'];

        if (!isImage) {
            flushImageGroup();
            result.push(message);
            continue;
        }

        if (batchId) {
            if (currentBatchId && currentBatchId !== batchId) {
                flushImageGroup();
            }
            if (!currentBatchId && currentImageGroup.length > 0) {
                flushImageGroup();
            }
            currentImageGroup.push(message);
            currentBatchId = batchId;
        } else {
            flushImageGroup();
            result.push(message);
        }
    }

    flushImageGroup();

    return result;
};

const MessageGroupComponent: React.FC<MessageGroupComponentProps> = React.memo(({
                                                                                    group,
                                                                                    selectedRoomId,
                                                                                    onReply,
                                                                                    onEdit,
                                                                                    onPrivateReply,
                                                                                    onForward,
                                                                                    onOpenForwardedOriginal,
                                                                                    sendReaction,
                                                                                    removeReaction,
                                                                                    getClient,
                                                                                    getCurrentUserId,
                                                                                    highlightedMessageId,
                                                                                    scrollToEvent,
                                                                                    isGroupChat,
                                                                                    isAiBotInRoom,
                                                                                    isRoomEncrypted,
                                                                                    aiBotJoinedAt,
                                                                                    searchQuery,
                                                                                    onOpenImageGallery,
                                                                                    onMediaLoad,
                                                                                    onViewProfile,
                                                                                    onOpenRoomMention,
                                                                                    onUnresolvedMention,
                                    callEntries,
                                    onCallBack,
                                                                                }) => {
    const messageIds = useMemo(() =>
        group.messages.map(m => `${m.getId()}:${m.replacingEventId() ?? ''}`).join(','),
    [group.messages]);

    const messageGroups = useMemo(() =>
        groupMessages(group.messages),
    [messageIds]);

    const isSystemEvent = (message: MatrixEvent) => {
        const eventType = message.getType();
        return eventType === 'm.room.member' ||
               eventType === 'm.room.name' ||
               eventType === 'm.room.topic';
    };

    return (
        <div className="message-group">
            <DateSeparator displayDate={group.displayDate}/>
            <div className="last:mb-5">
                {messageGroups.map((messagesInGroup, groupIndex) => {
                    if (messagesInGroup.length === 0) return;
                    const processedMessages = groupImageMessages(messagesInGroup);
                    console.log(processedMessages, group.displayDate)
                    return (
                        <div key={`group-${groupIndex}`} className="space-y-1">
                            {processedMessages.map((item, itemIndex) => {
                                if (Array.isArray(item)) {
                                    const imageMessages = item;
                                    const firstMessage = imageMessages[0];
                                    const isFirstInGroup = itemIndex === 0;
                                    const isLastInGroup = itemIndex === processedMessages.length - 1;
                                    return (
                                        <MessageBubble
                                            key={`image-group-${imageMessages.map(m => m.getId()).join('-')}`}
                                            message={firstMessage}
                                            imageGroup={imageMessages}
                                            roomId={selectedRoomId}
                                            onReply={onReply}
                                            onEdit={onEdit}
                                            onPrivateReply={onPrivateReply}
                                            onForward={onForward}
                                            onOpenForwardedOriginal={onOpenForwardedOriginal}
                                            sendReaction={sendReaction}
                                            removeReaction={removeReaction}
                                            getClient={getClient}
                                            getCurrentUserId={getCurrentUserId}
                                            highlightedMessageId={highlightedMessageId}
                                            scrollToEvent={scrollToEvent}
                                            showTimestamp={isFirstInGroup}
                                            isFirstInGroup={isFirstInGroup}
                                            isLastInGroup={isLastInGroup}
                                            isGroupChat={isGroupChat}
                                            isAiBotInRoom={isAiBotInRoom}
                                            isRoomEncrypted={isRoomEncrypted}
                                            aiBotJoinedAt={aiBotJoinedAt}
                                            searchQuery={searchQuery}
                                            onOpenImageGallery={onOpenImageGallery}
                                            onMediaLoad={onMediaLoad}
                                            onViewProfile={onViewProfile}
                                            onOpenRoomMention={onOpenRoomMention}
                                            onUnresolvedMention={onUnresolvedMention}
                                        />
                                    );
                                } else {
                                    const message = item;
                                    const isFirstInGroup = itemIndex === 0;
                                    const isLastInGroup = itemIndex === processedMessages.length - 1;

                                    if (isRtcNotificationEvent(message)) {
                                        return (
                                            <GroupCallTimelineRow
                                                key={message.getId()}
                                                message={message}
                                                roomId={selectedRoomId}
                                                getCurrentUserId={getCurrentUserId}
                                            />
                                        );
                                    }

                                    if (isCallInviteEvent(message)) {
                                        const entry = callEntries?.get(message.getId() || '');
                                        // Without the call's own events there is
                                        // nothing to say about it, so it draws
                                        // nothing rather than a blank row.
                                        if (!entry) return null;
                                        const room = getClient()?.getRoom?.(selectedRoomId);
                                        const memberName = (userId: string) => room?.getMember?.(userId)?.name || userId.split(':')[0].replace('@', '');
                                        const me = getCurrentUserId();
                                        const peerId = entry.caller !== me
                                            ? entry.caller
                                            : room?.getJoinedMembers?.().find((m: {userId: string}) => m.userId !== me)?.userId;
                                        return (
                                            <CallNotification
                                                key={message.getId()}
                                                entry={entry}
                                                onCallBack={onCallBack}
                                                peerName={peerId ? memberName(peerId) : undefined}
                                                callerLabel={isGroupChat ? (entry.caller === me ? 'You' : memberName(entry.caller)) : undefined}
                                            />
                                        );
                                    }

                                    if (isSystemEvent(message)) {
                                        return (
                                            <SystemMessage
                                                key={message.getId()}
                                                message={message}
                                                roomId={selectedRoomId}
                                                getClient={getClient}
                                            />
                                        );
                                    }

                                    return (
                                        <MessageBubble
                                            key={message.getId()}
                                            message={message}
                                            roomId={selectedRoomId}
                                            onReply={onReply}
                                            onEdit={onEdit}
                                            onPrivateReply={onPrivateReply}
                                            onForward={onForward}
                                            onOpenForwardedOriginal={onOpenForwardedOriginal}
                                            sendReaction={sendReaction}
                                            removeReaction={removeReaction}
                                            getClient={getClient}
                                            getCurrentUserId={getCurrentUserId}
                                            highlightedMessageId={highlightedMessageId}
                                            scrollToEvent={scrollToEvent}
                                            showTimestamp={isFirstInGroup}
                                            isFirstInGroup={isFirstInGroup}
                                            isLastInGroup={isLastInGroup}
                                            isGroupChat={isGroupChat}
                                            isAiBotInRoom={isAiBotInRoom}
                                            isRoomEncrypted={isRoomEncrypted}
                                            aiBotJoinedAt={aiBotJoinedAt}
                                            searchQuery={searchQuery}
                                            onOpenImageGallery={onOpenImageGallery}
                                            onMediaLoad={onMediaLoad}
                                            onViewProfile={onViewProfile}
                                            onOpenRoomMention={onOpenRoomMention}
                                            onUnresolvedMention={onUnresolvedMention}
                                        />
                                    );
                                }
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    if (prevProps.group.displayDate !== nextProps.group.displayDate) return false;
    if (prevProps.group.messages.length !== nextProps.group.messages.length) return false;
    if (prevProps.selectedRoomId !== nextProps.selectedRoomId) return false;
    if (prevProps.highlightedMessageId !== nextProps.highlightedMessageId) return false;
    if (prevProps.isGroupChat !== nextProps.isGroupChat) return false;
    if (prevProps.isAiBotInRoom !== nextProps.isAiBotInRoom) return false;
    if (prevProps.isRoomEncrypted !== nextProps.isRoomEncrypted) return false;
    if (prevProps.aiBotJoinedAt !== nextProps.aiBotJoinedAt) return false;
    if (prevProps.searchQuery !== nextProps.searchQuery) return false;
    // A call's row changes after its invite lands — the hangup is what turns
    // "Call" into "Missed call" — so the map's identity has to be compared.
    if (prevProps.callEntries !== nextProps.callEntries) return false;
    if (prevProps.onCallBack !== nextProps.onCallBack) return false;

    return prevProps.group.messages.every((msg, i) =>
        msg.getId() === nextProps.group.messages[i].getId()
        && msg.replacingEventId() === nextProps.group.messages[i].replacingEventId()
    );
});

export default MessageGroupComponent;
